import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, User, ArrowLeft } from 'lucide-react';
import axios from 'axios';
import SEO from '@/components/SEO';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BlogPost = () => {
  const { id } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPost = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${API}/blog/${id}`);
        setPost(response.data);
      } catch (err) {
        console.error('Erro ao carregar post:', err);
        setError('Post não encontrado');
      } finally {
        setLoading(false);
      }
    };

    fetchPost();
  }, [id]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white pt-32 pb-24 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block w-12 h-12 border-4 border-gray-200 border-t-[#FF3366] rounded-full animate-spin"></div>
          <p className="text-gray-500 mt-4">Carregando post...</p>
        </div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="min-h-screen bg-white pt-32 pb-24 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-500 text-lg mb-6">{error || 'Post não encontrado'}</p>
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 text-[#FF3366] font-semibold hover:gap-3 transition-all"
          >
            <ArrowLeft size={20} />
            Voltar ao Blog
          </Link>
        </div>
      </div>
    );
  }

  const blogPostingSchema = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `https://grantour360.com.br/blog/${post.id}`
    },
    "headline": post.title,
    "description": post.excerpt || "",
    "image": post.image_url || "https://grantour360.com.br/images/og-image.jpg",
    "author": {
      "@type": "Person",
      "name": post.author
    },
    "datePublished": post.published_at,
    "dateModified": post.published_at
  };

  return (
    <div className="min-h-screen bg-white pt-32 pb-24" data-testid="blog-post-page">
      <SEO 
        title={`${post.title} | Blog Gran Tour 360`}
        description={post.excerpt || ""}
        url={`https://grantour360.com.br/blog/${post.id}`}
        type="article"
        image={post.image_url || ""}
        schema={blogPostingSchema}
      />

      <article className="max-w-4xl mx-auto px-6 lg:px-8">
        <Link
          to="/blog"
          className="inline-flex items-center gap-2 text-gray-500 hover:text-[#FF3366] font-medium mb-8 transition-colors"
          data-testid="back-to-blog"
        >
          <ArrowLeft size={20} />
          Voltar ao Blog
        </Link>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="flex items-center gap-4 text-sm text-gray-400 mb-6">
            <span className="flex items-center gap-2">
              <Calendar size={16} />
              {formatDate(post.published_at)}
            </span>
            <span className="flex items-center gap-2">
              <User size={16} />
              {post.author}
            </span>
          </div>

          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-8 tracking-tight" data-testid="post-title">
            {post.title}
          </h1>

          <div className="rounded-lg overflow-hidden shadow-lg mb-8">
            <img
              src={post.image_url}
              alt={post.title}
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </div>

          <div className="prose prose-lg max-w-none" data-testid="post-content">
            <div className="text-gray-600 leading-relaxed whitespace-pre-wrap text-base sm:text-lg">
              {post.content}
            </div>
          </div>
        </motion.div>
      </article>
    </div>
  );
};

export default BlogPost;
